import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { EventCreate, EventStatus, Event } from '../types';
import { apiService } from '../services/api';

interface EventFormProps {
  onSubmit: (data: EventCreate, imageFile?: File) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
  initialData?: Event;
  onFileUpload?: (eventId: number, files: File[]) => Promise<void>;
  onFileDelete?: (eventId: number, fileIndex: number) => Promise<void>;
}

export const EventForm: React.FC<EventFormProps> = ({
  onSubmit,
  onCancel,
  loading,
  error,
  initialData,
  onFileUpload,
  onFileDelete,
}) => {
  const [formData, setFormData] = useState<EventCreate>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    event_date: initialData?.event_date ? new Date(initialData.event_date).toISOString().slice(0, 16) : '',
    location: initialData?.location || '',
    status: (initialData?.status as EventStatus) || EventStatus.DRAFT,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  console.log(uploadingFiles);
  const isEditing = !!initialData?.id;

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        event_date: initialData.event_date ? new Date(initialData.event_date).toISOString().slice(0, 16) : '',
        location: initialData.location || '',
        status: (initialData.status as EventStatus) || EventStatus.DRAFT,
      });
      setImageFile(null);
      setFiles([]);
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.event_date)
      return;
    const result = await onSubmit(formData, imageFile || undefined);
    // Files will be uploaded separately after event creation/update
    // For new events, we need the created event ID
    const eventId = initialData?.id || (result as any)?.id;
    if (files.length > 0 && eventId && onFileUpload) {
      setUploadingFiles(true);
      try {
        await onFileUpload(eventId, files);
        setFiles([]);
      } catch (err) {
        console.error('Error uploading files:', err);
      } finally {
        setUploadingFiles(false);
      }
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...fileArray]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingFile = async (fileIndex: number) => {
    if (initialData?.id && onFileDelete) {
      try {
        await onFileDelete(initialData.id, fileIndex);
      } catch (err) {
        console.error('Error deleting file:', err);
      }
    }
  };

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
      <div className='flex justify-between items-center mb-6'>
        <h2 className='text-lg font-semibold text-gray-900'>
          {isEditing ? 'Modifier l\'événement' : 'Créer un nouvel événement'}
        </h2>
      </div>

      {error && (
        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
          <p className='text-red-600 text-sm'>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Titre *
          </label>
          <input
            type='text'
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder="Titre de l'événement"
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Date et heure *
          </label>
          <input
            type='datetime-local'
            value={formData.event_date}
            onChange={(e) =>
              setFormData({ ...formData, event_date: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            required
            lang='fr-FR'
          />
          <p className='text-xs text-gray-500 mt-1'>
            Format : JJ/MM/AAAA HH:MM
          </p>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Lieu
          </label>
          <input
            type='text'
            value={formData.location || ''}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder="Lieu de l'événement"
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Description *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            rows={8}
            placeholder="Description de l'événement (Markdown supporté)"
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Statut
          </label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData({
                ...formData,
                status: e.target.value as EventStatus,
              })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            <option value={EventStatus.DRAFT}>Brouillon</option>
            <option value={EventStatus.PUBLISHED}>Publié</option>
          </select>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Image (optionnel)
          </label>
          {initialData?.image_path && (
            <div className='mb-3'>
              <p className='text-sm text-gray-600 mb-2'>Image actuelle :</p>
              <img
                src={apiService.getEventImageUrl(initialData.id)}
                alt={initialData.title}
                className='w-full max-w-md h-auto rounded-lg border border-gray-200'
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
          <input
            type='file'
            accept='image/jpeg,image/png,image/gif,image/webp'
            onChange={handleImageChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          {imageFile && (
            <p className='mt-1 text-sm text-gray-500'>
              Nouveau fichier sélectionné: {imageFile.name}
            </p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Fichiers joints {isEditing ? '(optionnel)' : ''}
          </label>
          <input
            type='file'
            multiple
            onChange={handleFilesChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          <p className='mt-1 text-xs text-gray-500'>
            {isEditing 
              ? 'Sélectionnez des fichiers à ajouter à cet événement'
              : 'Sélectionnez des fichiers à joindre à cet événement (seront uploadés après création)'}
          </p>
          {files.length > 0 && (
            <div className='mt-2 space-y-1'>
              {files.map((file, index) => (
                <div key={index} className='flex items-center justify-between p-2 bg-gray-50 rounded'>
                  <span className='text-sm text-gray-700'>{file.name}</span>
                  <button
                    type='button'
                    onClick={() => handleRemoveFile(index)}
                    className='text-red-600 hover:text-red-800 text-sm'
                  >
                    <X className='w-4 h-4' />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {isEditing && initialData?.files && initialData.files.length > 0 && (
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Fichiers existants
            </label>
            <div className='space-y-1'>
              {initialData.files.map((file, index) => {
                const fileName = file.split('/').pop() || `Fichier ${index + 1}`;
                return (
                  <div key={index} className='flex items-center justify-between p-2 bg-gray-50 rounded'>
                    <a
                      href={apiService.getEventFileUrl(initialData.id, index)}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-sm text-blue-600 hover:text-blue-800'
                    >
                      {fileName}
                    </a>
                    {onFileDelete && (
                      <button
                        type='button'
                        onClick={() => handleDeleteExistingFile(index)}
                        className='text-red-600 hover:text-red-800 text-sm'
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className='flex gap-3 pt-4'>
          <button
            type='submit'
            disabled={
              loading ||
              uploadingFiles ||
              !formData.title ||
              !formData.description ||
              !formData.event_date
            }
            className='flex-1 bg-church-brown text-white py-3 px-6 rounded-lg hover:bg-church-dark transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg disabled:shadow-none'
          >
            {loading || uploadingFiles
              ? isEditing
                ? 'Mise à jour...'
                : 'Création...'
              : isEditing
              ? 'Mettre à jour'
              : 'Créer l\'événement'}
          </button>
          <button
            type='button'
            onClick={onCancel}
            className='px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium'
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
